import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import GmailConnected from "./pages/GmailConnected";
import OutlookConnected from "./pages/OutlookConnected";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Subscriptions from "./pages/Subscriptions";
import SubscriptionDetail from "./pages/SubscriptionDetail";
import AddSubscription from "./pages/AddSubscription";
import CancelWorkflow from "./pages/CancelWorkflow";
import Negotiate from "./pages/Negotiate";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import ChatAssistant from "./pages/ChatAssistant";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import EarlyAccess from "./pages/EarlyAccess";
import CancelHelp from "./pages/CancelHelp";
import FeatureRequestsPage from "./pages/FeatureRequestsPage";

// Protected Route wrapper - redirects to onboarding if not completed
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // If user data passed from AuthCallback, skip auth check
  if (location.state?.user) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#064E3B]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Pages that should always be accessible (skip onboarding check)
  const bypassOnboardingPaths = [
    '/onboarding',
    '/settings', 
    '/gmail-connected',
    '/outlook-connected',
    '/pricing',
    '/dashboard',
    '/subscriptions',
    '/add-subscription'
  ];

  // Check if onboarding is needed (but allow access to certain pages)
  if (user && !user.onboarding_completed && 
      !bypassOnboardingPaths.some(path => location.pathname.startsWith(path))) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Public Route wrapper (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#064E3B]"></div>
      </div>
    );
  }

  // Don't redirect if on pricing page (allow cancelled payments to stay)
  if (isAuthenticated && location.pathname !== '/pricing') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Router component that handles OAuth callback detection
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/early-access" element={<EarlyAccess />} />
      <Route path="/cancel-help" element={<CancelHelp />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/gmail-connected" element={<GmailConnected />} />
      <Route path="/outlook-connected" element={<OutlookConnected />} />
      
      {/* Signup route - redirects to login with referral code */}
      <Route 
        path="/signup" 
        element={
          <PublicRoute>
            <Login isSignup={true} />
          </PublicRoute>
        } 
      />
      
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <Subscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions/:id"
        element={
          <ProtectedRoute>
            <SubscriptionDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-subscription"
        element={
          <ProtectedRoute>
            <AddSubscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cancel/:id"
        element={
          <ProtectedRoute>
            <CancelWorkflow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/negotiate/:id"
        element={
          <ProtectedRoute>
            <Negotiate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatAssistant />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feature-requests"
        element={
          <ProtectedRoute>
            <FeatureRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
